import type { FolderDetailRow } from '@listening/frontend/query/folders';
import { cn } from '@listening/utils/react-helpers';
import FolderOutlinedIcon from '@listening/assets/icons/folder-outlined.svg?react';

import {
  Dialog,
  DialogTrigger,
} from '@listening/frontend/components/radix/dialog';
import { EditPencil, NavArrowLeft, Plus, Xmark } from 'iconoir-react';
import {
  createFileRoute,
  redirect,
  useParams,
  useRouter,
} from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { DashboardQueryError } from '@/components/shared-ui/dashboard-query-error';
import { FileCardV2 } from '@/components/web-fe/design-v2/file-card-v2';
import { ViewModeToggle } from '@/components/web-fe/design-v2/home-v2';
import {
  FOLDER_SORT_MODES,
  folderSortMode,
  sortLabel,
} from '@/components/web-fe/design-v2/library-sort';
import {
  useLibrarySortMode,
  useSetLibrarySortMode,
} from '@listening/frontend/stores/library-sort-store';
import { FolderSkeleton } from '@/components/web-fe/design-v2/skeletons';
import { SortDropdown } from '@/components/web-fe/design-v2/sort-dropdown';
import {
  fieldSurface,
  primaryButtonSurface,
  secondaryButtonSurface,
} from '@/components/web-fe/design-v2/surfaces';
import { DialogCloseProvider } from '@listening/frontend/components/custom/dialog-dropdown';
import { AddFilesToFolderDialog } from '@/components/web-fe/dialogs/add-files-to-folder-dialog';
import { DeleteFolderDialog } from '@/components/web-fe/dialogs/delete-folder-dialog';
import { DashboardPage } from '@/components/web-fe/pages/dashboard/dashboard-page';
import {
  flattenAudioPages,
  folderAudiosInfiniteArgs,
  useFolderAudios,
} from '@listening/frontend/query/audio-list';
import { NotFoundError } from '@listening/api-contract';
import { Data, Effect, Result } from 'effect';
import { eq } from '@/runtimes/effect-query';
import { getRuntime } from '@/runtimes/get-runtime';
import { unwrapEffectQueryFailure } from '@listening/frontend/query/query-error';
import { useElementOnScreen } from '@/lib/utils';
import * as m from '@/paraglide/messages';
import {
  folderDetailQueryOptions,
  folderService,
} from '@/services/folder-service';
import { useLocalUserPrefsStore } from '@/services/user-service';

// Best-effort prefetch failures are captured (never thrown) — see the loader
// comment below — so the specific cause doesn't matter beyond being typed and
// carrying the original rejection for the folder-not-found check.
class FolderPrefetchError extends Data.TaggedError('FolderPrefetchError')<{
  cause: unknown;
}> {}

export const Route = createFileRoute(
  '/app/_authenticated/_dashboard/files/$folderId',
)({
  component: Page,
  pendingComponent: FolderPendingSkeleton,
  pendingMs: 0,
  errorComponent: DashboardQueryError,
  // Prefetch the folder detail + the audio list both views derive from straight
  // into TanStack Query (no manual setQueryData) so the suspense reads below
  // resolve warm. A typed 404 on the folder detail means it doesn't exist (or
  // isn't ours) → redirect to the files root. Any OTHER failure (incl. the
  // audio-list prefetch) is best-effort: the component's suspense read re-runs
  // and surfaces a scoped, typed error via this route's `errorComponent` instead
  // of bubbling to the root catch boundary. `mode: 'result'` runs BOTH
  // prefetches to completion (no short-circuit) and returns each as a `Result`,
  // so the redirect decision keys off the FOLDER-DETAIL slot specifically — a
  // failed audio-list prefetch must never be mistaken for "folder not found".
  // `FolderPrefetchError.cause` carries the raw rejection (possibly an
  // `EffectQueryFailure` envelope), so unwrap one level before checking the
  // tag. `redirect` stays plain TanStack control flow rather than routing
  // through `runPromise` (which would wrap it).
  loader: async ({ context: { queryClient }, params }) => {
    const { folderId } = params;
    const [folderDetailResult] = await getRuntime().runPromise(
      Effect.all(
        [
          Effect.tryPromise({
            try: () =>
              queryClient.ensureQueryData(folderDetailQueryOptions(folderId)),
            catch: (cause) => new FolderPrefetchError({ cause }),
          }),
          Effect.tryPromise({
            try: () =>
              queryClient.ensureInfiniteQueryData(
                eq.infiniteQueryOptions(folderAudiosInfiniteArgs(folderId)),
              ),
            catch: (cause) => new FolderPrefetchError({ cause }),
          }),
        ],
        { concurrency: 'unbounded', mode: 'result' },
      ),
    );
    const isNotFound =
      Result.isFailure(folderDetailResult) &&
      unwrapEffectQueryFailure(folderDetailResult.failure.cause) instanceof
        NotFoundError;
    if (isNotFound) throw redirect({ to: '/app/home' });

    // The API also accepts a folder's pre-cutover Mongo ObjectId, so `folderId`
    // here may not be the canonical uuid. Bounce to the canonical URL rather than
    // rendering under the legacy one: every folder mutation invalidates the
    // uuid-keyed caches, so a legacy-keyed page would silently go stale.
    if (
      Result.isSuccess(folderDetailResult) &&
      folderDetailResult.success.id !== folderId
    ) {
      throw redirect({
        to: '/app/files/$folderId',
        params: { folderId: folderDetailResult.success.id },
        replace: true,
      });
    }
  },
});

function Page() {
  const { folderId } = useParams({
    from: '/app/_authenticated/_dashboard/files/$folderId',
  });
  const folderDetail = useSuspenseQuery(
    folderDetailQueryOptions(folderId),
  ).data;

  return <LoadedPage folderDetail={folderDetail} />;
}

const actionButton =
  'flex h-9 items-center gap-1.5 px-3.5 text-[13px] font-medium';

function LoadedPage({ folderDetail }: { folderDetail: FolderDetailRow }) {
  // Server-scoped to this folder + ready status (the query args); infinite,
  // loaded incrementally via the scroll sentinel below.
  const query = useFolderAudios(folderDetail.id);
  const audioItems = flattenAudioPages(query.data);
  const sortMode = folderSortMode(useLibrarySortMode());
  const setSortMode = useSetLibrarySortMode();
  const [sentinelRef, sentinelVisible] = useElementOnScreen();
  if (query.hasNextPage && !query.isFetchingNextPage && sentinelVisible)
    void query.fetchNextPage();

  const viewMode = useLocalUserPrefsStore((s) => s.homeScreenLayout);
  const visibleItems = audioItems;

  const folderName = folderService.utils.folderPathToString(folderDetail.path);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isAddFilesOpen, setIsAddFilesOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState(folderName);
  const router = useRouter();
  const updateFolderMutation = folderService.mutations.useUpdateMutation();

  return (
    <DashboardPage>
      <div className='flex flex-col gap-[var(--v2-gap-6)]'>
        <div className='flex flex-wrap items-center justify-between gap-[var(--v2-gap-3)]'>
          <div className='flex min-w-0 grow items-center gap-2'>
            <button
              type='button'
              aria-label={m.common_back()}
              onClick={() => {
                router.history.back();
              }}
              className='flex size-8 shrink-0 items-center justify-center rounded-full text-[var(--v2-text-primary)] transition-colors hover:bg-[var(--v2-chip-bg)]'
            >
              <NavArrowLeft className='size-5' />
            </button>
            {isEditMode ? (
              <input
                disabled={updateFolderMutation.isPending}
                value={newFolderName}
                onChange={(e) => {
                  setNewFolderName(e.target.value);
                }}
                aria-label={m.common_edit()}
                className={cn(
                  fieldSurface,
                  'h-9 w-full max-w-sm px-3 text-[15px] font-medium outline-none focus:border-[var(--v2-outline-strong)]',
                )}
              />
            ) : (
              <>
                <h1
                  className='min-w-0 truncate text-[22px] text-[var(--v2-text-primary)]'
                  style={{ fontWeight: 'var(--v2-heading-weight)' }}
                >
                  {folderName}
                </h1>
                <span className='rounded-full bg-[var(--v2-chip-bg)] px-2 py-0.5 text-[12px] font-medium text-[var(--v2-chip-text)] tabular-nums'>
                  {folderDetail.num_items}
                </span>
              </>
            )}
          </div>
          <div className='flex flex-wrap items-center gap-[var(--v2-gap-3)]'>
            {isEditMode ? (
              <>
                <button
                  type='button'
                  className={cn(secondaryButtonSurface, actionButton)}
                  disabled={updateFolderMutation.isPending}
                  onClick={() => {
                    setIsEditMode(false);
                  }}
                >
                  <Xmark className='size-3.5' />
                  {m.common_cancel()}
                </button>
                <button
                  type='button'
                  className={cn(primaryButtonSurface, actionButton)}
                  disabled={updateFolderMutation.isPending}
                  onClick={() => {
                    updateFolderMutation.mutate(
                      {
                        id: folderDetail.id,
                        path: newFolderName.split('/'),
                      },
                      {
                        onSuccess: () => {
                          setIsEditMode(false);
                        },
                      },
                    );
                  }}
                >
                  {m.common_save()}
                </button>
              </>
            ) : (
              <>
                <DialogCloseProvider
                  open={isAddFilesOpen}
                  onOpenChange={setIsAddFilesOpen}
                >
                  <DialogTrigger asChild>
                    <button
                      type='button'
                      className={cn(primaryButtonSurface, actionButton)}
                    >
                      <Plus className='size-3.5' />
                      {m.files_add_files()}
                    </button>
                  </DialogTrigger>
                  {isAddFilesOpen && (
                    <AddFilesToFolderDialog
                      folder={{ id: folderDetail.id, name: folderName }}
                    />
                  )}
                </DialogCloseProvider>
                <button
                  type='button'
                  className={cn(secondaryButtonSurface, actionButton)}
                  onClick={() => {
                    setNewFolderName(folderName);
                    setIsEditMode(true);
                  }}
                >
                  <EditPencil className='size-3.5' />
                  {m.common_edit()}
                </button>
                <Dialog>
                  <DialogTrigger asChild>
                    <button
                      type='button'
                      className={cn(
                        secondaryButtonSurface,
                        actionButton,
                        'text-scarlet-400',
                      )}
                    >
                      {m.common_delete()}
                    </button>
                  </DialogTrigger>
                  <DeleteFolderDialog
                    folder={{
                      count: folderDetail.num_items,
                      name: folderName,
                      id: folderDetail.id,
                    }}
                  />
                </Dialog>
              </>
            )}
            <ViewModeToggle />
            <SortDropdown
              value={sortMode}
              options={FOLDER_SORT_MODES}
              labelOf={sortLabel}
              onChange={setSortMode}
            />
          </div>
        </div>

        {visibleItems.length === 0 ? (
          <div className='flex flex-col items-center gap-2 py-16 text-center'>
            <FolderOutlinedIcon className='size-10 text-[var(--v2-text-muted)]' />
            <h3
              className='text-[15px] text-[var(--v2-text-primary)]'
              style={{ fontWeight: 'var(--v2-heading-weight)' }}
            >
              {m.files_no_files_in_folder({ path: folderName })}
            </h3>
            <p className='max-w-sm text-[13px] text-[var(--v2-text-muted)]'>
              {m.files_empty_folder_description()}
            </p>
          </div>
        ) : viewMode === 'list' ? (
          <div className='flex flex-col'>
            {visibleItems.map((audioItem) => (
              <FileCardV2 key={audioItem.id} audioItem={audioItem} asRow />
            ))}
          </div>
        ) : (
          <div
            className='grid gap-[var(--v2-grid-gap)]'
            style={{
              gridTemplateColumns:
                'repeat(auto-fill, minmax(var(--v2-grid-min), 1fr))',
            }}
          >
            {visibleItems.map((audioItem) => (
              <FileCardV2 key={audioItem.id} audioItem={audioItem} />
            ))}
          </div>
        )}
        <div ref={sentinelRef} />
      </div>
    </DashboardPage>
  );
}

function FolderPendingSkeleton() {
  return (
    <DashboardPage>
      <FolderSkeleton />
    </DashboardPage>
  );
}
